export const SECRET_SETTING_KEYS = new Set<string>(['openai_api_key']);

export type StudioSearchParams = {
  canvasAssetId: string | undefined;
  jobId: string | undefined;
  outputIndex: number | undefined;
};

export const createStudioDefaultSearch = (): StudioSearchParams => ({
  canvasAssetId: undefined,
  jobId: undefined,
  outputIndex: undefined,
});

export type BillingSearchParams = {
  section: string | undefined;
};

export const createBillingDefaultSearch = (): BillingSearchParams => ({
  section: undefined,
});

const parseBooleanFlag = (value: string | undefined, defaultValue = false) => {
  if (value === undefined) {
    return defaultValue;
  }
  return value.trim().toLowerCase() === 'true';
};

export const STUDIO_CANVAS_ENABLED = parseBooleanFlag(
  import.meta.env.VITE_ENABLE_STUDIO_CANVAS,
  true
);

export const STUDIO_PROMPT_BAR_ENABLED = parseBooleanFlag(
  import.meta.env.VITE_ENABLE_STUDIO_PROMPT_BAR,
  true
);

export const FALLBACK_HIDDEN_SETTING_KEYS = [
  'content_moderation',
  'disable_safety_checker',
  'height',
  'max_images',
  'moderation',
  'output_format',
  'prompt_upsampling',
  'safety_filter_level',
  'safety_tolerance',
  'sequential_image_generation',
  'sync_mode',
  'user_id',
  'video_id',
  'width',
] as const;

export const FALLBACK_RELAXED_BOOLEAN_DEFAULTS: Record<string, boolean> = {
  content_moderation: false,
  disable_safety_checker: true,
  prompt_upsampling: false,
};

export const FALLBACK_RELAXED_NUMERIC_MAX_KEYS = ['safety_tolerance'] as const;

export const FALLBACK_RELAXED_ENUM_PREFERENCES: Record<string, string[]> = {
  moderation: ['off', 'disabled', 'none', 'lenient', 'light', 'low'],
  safety_filter_level: ['block_only_high', 'lenient', 'low', 'minimal'],
  output_format: ['png', 'jpg', 'jpeg'],
  sequential_image_generation: ['disabled'],
};

export const NORMALIZED_LABEL_OVERRIDES: Record<string, string> = {
  audio_url: 'Reference audio',
  bit_rate: 'Audio bitrate',
  cfg_scale: 'Prompt adherence',
  enable_prompt_expansion: 'Prompt rewrite',
  enhance_prompt: 'Prompt rewrite',
  fast_mode: 'Fast mode',
  first_frame_image: 'Starting frame',
  first_frame_url: 'Starting frame',
  generate_audio: 'Generate audio',
  go_fast: 'Fast mode',
  guidance: 'Prompt adherence',
  image_url: 'Reference image',
  input_image: 'Source image',
  input_images: 'Source images',
  language_code: 'Language',
  negative_prompt: 'What to avoid',
  number_of_images: 'Outputs',
  optimize_prompt: 'Prompt rewrite',
  magic_prompt_option: 'Prompt rewrite',
  prompt_optimizer: 'Prompt rewrite',
  quality: 'Render quality',
  num_inference_steps: 'Quality steps',
  num_images: 'Outputs',
  num_outputs: 'Outputs',
  output_format: 'File format',
  safety_tolerance: 'Safety level',
  sample_guide_scale: 'Prompt adherence',
  sample_rate: 'Audio sample rate',
  sample_steps: 'Quality steps',
  sound_effect_content: 'Sound effect prompt',
  sound_effect_switch: 'Sound effects',
  start_image: 'Starting image',
  start_image_url: 'Starting image',
  end_image: 'Ending image',
  end_image_url: 'Ending image',
  steps: 'Quality steps',
  style_type: 'Style preset',
};

export const FALLBACK_LABEL_OVERRIDES: Record<string, string> = {
  aspect_ratio: 'Canvas ratio',
  audio_url: 'Reference audio',
  bitrate: 'Audio bitrate',
  cfg: 'Prompt adherence',
  cfg_scale: 'Prompt adherence',
  duration: 'Clip length',
  emotion: 'Voice emotion',
  end_image: 'Ending image',
  end_image_url: 'Ending image',
  end_frame: 'Ending frame',
  enhance_prompt: 'Prompt rewrite',
  fast_mode: 'Fast mode',
  first_frame_image: 'Starting frame',
  first_frame_url: 'Starting frame',
  force_rmbg: 'Remove background',
  fps: 'Frames per second',
  frame_rate: 'Frame rate',
  frames_per_second: 'Frame rate',
  generate_audio: 'Generate audio',
  go_fast: 'Fast mode',
  guidance: 'Prompt adherence',
  guidance_scale: 'Style strength',
  image: 'Source image',
  image_size: 'Canvas preset',
  image_url: 'Reference image',
  image_urls: 'Reference images',
  input_image: 'Source image',
  input_images: 'Source images',
  last_frame_url: 'Ending frame',
  last_frame_image: 'Ending frame',
  language_code: 'Language',
  limit_generations: 'Single image only',
  magic_prompt_option: 'Prompt rewrite',
  megapixels: 'Output size',
  motion_mode: 'Motion style',
  negative_prompt: 'What to avoid',
  num_inference_steps: 'Quality steps',
  num_images: 'Outputs',
  num_outputs: 'Outputs',
  number_of_images: 'Outputs',
  optimize_prompt: 'Prompt rewrite',
  output_format: 'File format',
  pitch: 'Voice pitch',
  prompt_enhance: 'Prompt rewrite',
  prompt_optimizer: 'Prompt rewrite',
  prompt_strength: 'Prompt influence',
  quality: 'Render quality',
  safety_tolerance: 'Safety level',
  sample_guide_scale: 'Prompt adherence',
  sample_rate: 'Audio sample rate',
  sample_steps: 'Quality steps',
  seed: 'Random seed',
  size: 'Resolution',
  resolution: 'Resolution',
  sound_effect_content: 'Sound effect prompt',
  sound_effect_switch: 'Sound effects',
  speed: 'Speech speed',
  start_image: 'Starting image',
  start_image_url: 'Starting image',
  start_frame: 'Starting frame',
  steps: 'Quality steps',
  style_preset: 'Style preset',
  style_type: 'Style preset',
  upscale_factor: 'Upscale amount',
  video_url: 'Reference video',
  voice_id: 'Voice',
};
